import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, LinearProgress, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { AlertTriangle, Download, MoreHorizontal, Pencil, RotateCcw, ScanText, Sparkles, Trash2, Upload, UserPlus, Wand2, X, XCircle } from "lucide-react";
import sh from "bundles/common/utils/sh";
import CvViewer from "bundles/Internal/components/common/CvViewer";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import { DEFAULT_PARSER_MODEL, PARSER_MODELS, parserModelLabel } from "bundles/Internal/components/common/ParserModelMenu";
import { GenerateCvSplitButton, RegenerateCvDialog } from "bundles/Internal/components/common/BrandedCvMenu";
import { incompleteMessage, isIncomplete, missingLabels } from "bundles/Internal/utils/brandedCv";
import BrandedCvEditor from "./BrandedCvEditor";
import { STATUS_TONE } from "./VaultListRow";
import { errorCopy, runningLabel, stageDetail, stageProgress } from "./ParseStatusPanel";
import { brandedErrorCopy, brandedStageDetail, brandedStageLabel, brandedStageProgress } from "./BrandedCvStatusPanel";

const RUNNING_STATUSES = ["uploaded", "extracting_text", "ocring", "queued", "parsing"];

const primaryButtonSx = {
  textTransform: "none",
  fontSize: 12.5,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  borderRadius: "calc(-2px + 0.75rem)",
  px: 1.5,
  height: 30,
  flexShrink: 0,
  color: "hsl(80 97% 72%)",
  backgroundColor: "hsl(150 38% 17%)",
  "& .MuiButton-startIcon": { mr: 0.5 },
  "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
};

const ghostButtonSx = {
  textTransform: "none",
  fontSize: 12.5,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  borderRadius: "calc(-2px + 0.75rem)",
  px: 1.5,
  height: 30,
  flexShrink: 0,
  color: "hsl(150 30% 12%)",
  backgroundColor: "#ffffff",
  border: "1px solid hsl(140 8% 88%)",
  "& .MuiButton-startIcon": { mr: 0.5 },
  "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
};

const ViewerTabs = ({ value, onChange, hasDocument, hasText, hasBranded }) => (
  <Box
    sx={{
      display: "flex",
      flexShrink: 0,
      p: "2px",
      gap: "2px",
      borderRadius: "8px",
      backgroundColor: "hsl(140 8% 92%)",
    }}
  >
    {[
      { key: "document", label: "Document", enabled: hasDocument },
      { key: "text", label: "Text", enabled: hasText },
      { key: "branded", label: "Squarehub CV", enabled: hasBranded },
    ].map((tab) => (
      <Box
        key={tab.key}
        component="button"
        type="button"
        disabled={!tab.enabled}
        onClick={() => tab.enabled && onChange(tab.key)}
        sx={{
          all: "unset",
          px: 1.25,
          height: 24,
          display: "flex",
          alignItems: "center",
          borderRadius: "6px",
          fontSize: 11.5,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          cursor: tab.enabled ? "pointer" : "default",
          opacity: tab.enabled ? 1 : 0.4,
          color: value === tab.key ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
          backgroundColor: value === tab.key ? "#ffffff" : "transparent",
          boxShadow: value === tab.key ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
        }}
      >
        {tab.label}
      </Box>
    ))}
  </Box>
);

const StatusStrip = ({ item }) => {
  const running = ["queued", "parsing", "ocring"].includes(item.status) && !item.stalled;
  const failed = item.status === "failed" || item.status === "needs_ocr" || Boolean(item.stalled);
  const duplicate = Boolean(item.duplicate_of) && item.status !== "converted";

  if (!duplicate && !running && !failed) return null;

  const copy = failed
    ? errorCopy({
      status: item.status,
      errorKind: item.error_kind,
      error: item.error,
      stalled: item.stalled,
      ocrable: item.ocr_retryable,
    })
    : null;

  const tone = failed
    ? { bg: "hsl(0 80% 98%)", border: "hsl(0 60% 88%)", text: "hsl(0 55% 38%)" }
    : running
      ? { bg: "hsl(217 91% 97%)", border: "hsl(217 60% 88%)", text: "hsl(217 70% 35%)" }
      : { bg: "hsl(36 90% 97%)", border: "hsl(36 90% 80%)", text: "hsl(35 60% 28%)" };

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 1.5,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderBottom: `1px solid ${tone.border}`,
        backgroundColor: tone.bg,
      }}
    >
      {!running && <AlertTriangle size={13} color={tone.text} style={{ flexShrink: 0 }} />}
      <Typography noWrap sx={{ fontSize: 11.5, color: tone.text, flexShrink: 0 }}>
        {failed && copy.title}
        {running && runningLabel(item)}
        {running && stageDetail(item) ? ` · ${stageDetail(item)}` : ""}
        {duplicate && (
          <>
            {"Matches another candidate's email: "}
            <Box
              component={Link}
              to={`/internal/candidates/${item.duplicate_of.id}`}
              sx={{
                color: "inherit",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                textDecoration: "underline",
                "&:hover": { textDecoration: "none" },
              }}
            >
              {item.duplicate_of.name}
            </Box>
          </>
        )}
      </Typography>
      {running && (
        <LinearProgress
          variant="determinate"
          value={stageProgress(item)}
          sx={{
            flex: 1,
            height: 2,
            borderRadius: 2,
            backgroundColor: "hsl(217 40% 90%)",
            "& .MuiLinearProgress-bar": { backgroundColor: "hsl(217 91% 55%)" },
          }}
        />
      )}
    </Box>
  );
};

const EmptyState = () => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      px: 4,
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "hsl(140 10% 92%)",
      }}
    >
      <Upload size={22} color="hsl(150 10% 46%)" />
    </Box>
    <Typography sx={{ fontSize: 15, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", mt: 0.5 }}>
      Nothing open
    </Typography>
    <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)", maxWidth: 320 }}>
      Pick a CV on the left to read it and turn it into a candidate, or drop new ones anywhere on this page.
    </Typography>
  </Box>
);

const BrandedCvPane = ({ itemId, status, stage, progress, stalled, error, version, onRegenerate }) => {
  if (status === "generating") {
    if (stalled) {
      const copy = brandedErrorCopy({ stalled: true });
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, px: 4, textAlign: "center" }}>
          <AlertTriangle size={20} color="hsl(35 60% 40%)" />
          <Typography sx={{ fontSize: 13.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            {copy.title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", maxWidth: 380 }}>
            {copy.detail}
          </Typography>
        </Box>
      );
    }

    const detail = brandedStageDetail(stage, progress);
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 4 }}>
        <CircularProgress size={22} sx={{ color: "hsl(150 38% 17%)" }} />
        <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>
          {brandedStageLabel(stage)}
          {detail ? ` · ${detail}` : ""}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={brandedStageProgress(stage)}
          sx={{
            width: 180,
            height: 2,
            borderRadius: 2,
            backgroundColor: "hsl(140 8% 90%)",
            "& .MuiLinearProgress-bar": { backgroundColor: "hsl(150 38% 17%)" },
          }}
        />
      </Box>
    );
  }

  if (status === "failed") {
    const copy = brandedErrorCopy({ errorKind: progress?.error_kind, error });
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, px: 4, textAlign: "center" }}>
        <AlertTriangle size={20} color="hsl(0 55% 45%)" />
        <Typography sx={{ fontSize: 13.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
          {copy.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", maxWidth: 380 }}>
          {copy.detail}
        </Typography>
        {onRegenerate && (
          <Button size="small" disableRipple startIcon={<RotateCcw size={14} />} onClick={onRegenerate} sx={{ mt: 0.5, ...ghostButtonSx }}>
            Regenerate
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      key={version}
      component="embed"
      src={`/internal/cv_vault_items/${itemId}/branded_cv/download?v=${version}#toolbar=0&navpanes=0`}
      type="application/pdf"
      sx={{ flex: 1, width: "100%", border: "none" }}
    />
  );
};

const ReparseCvDialog = ({ open, onClose, onConfirm }) => {
  const [model, setModel] = useState(DEFAULT_PARSER_MODEL);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm(model)}
      title="Parse this CV again"
      description="The existing extraction is replaced by a fresh read of the document."
      cancelLabel="Not now"
      confirmLabel="Parse again"
    >
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ mb: 0.5, fontSize: 12, color: "hsl(150 10% 46%)" }}>AI model</Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {PARSER_MODELS.map((option) => {
            const active = option.value === model;
            return (
              <Button
                key={option.value}
                size="small"
                disableRipple
                onClick={() => setModel(option.value)}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  px: 1.25,
                  minWidth: 0,
                  borderRadius: "8px",
                  color: active ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
                  backgroundColor: active ? "hsl(150 38% 17%)" : "#ffffff",
                  border: "1px solid hsl(140 8% 88%)",
                  "&:hover": { backgroundColor: active ? "hsl(150 38% 22%)" : "hsl(140 10% 96%)" },
                }}
              >
                {parserModelLabel(option.value)}
              </Button>
            );
          })}
        </Box>
      </Box>
    </ConfirmDialog>
  );
};

const VaultDetail = ({
  item,
  parserModel,
  onExtract,
  onConvert,
  onCancel,
  onRetry,
  onOcr,
  onDelete,
  onGenerateBranded,
  onClose,
  editing,
  onEditingChange,
}) => {
  const [detail, setDetail] = useState(null);
  const [branded, setBranded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [tab, setTab] = useState("document");
  const [brandedVersion, setBrandedVersion] = useState(0);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [reparseOpen, setReparseOpen] = useState(false);
  const watchedRef = useRef(null);
  const itemId = item?.id;
  const brandedStatus = item?.branded_cv_status || null;
  const brandedIncomplete = brandedStatus === "ready" && isIncomplete(branded?.data);

  useEffect(() => {
    if (!itemId) {
      setDetail(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setDetail(null);
    setTab("document");
    onEditingChange(false);
    setRegenerateOpen(false);
    setReparseOpen(false);

    sh.get(`/internal/cv_vault_items/${itemId}`)
      .then((res) => {
        if (!active) return;
        setDetail(res.data || {});
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [itemId]);

  // The pane above loads once per item, but OCR and extraction write cv_text after that. Refetch
  // when an item we watched running settles — not on open, which the first effect already covers.
  useEffect(() => {
    if (!itemId) return undefined;

    if (RUNNING_STATUSES.includes(item?.status)) {
      watchedRef.current = itemId;
      return undefined;
    }
    if (watchedRef.current !== itemId) return undefined;
    watchedRef.current = null;

    let active = true;
    sh.get(`/internal/cv_vault_items/${itemId}`)
      .then((res) => active && setDetail(res.data || {}))
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [itemId, item?.status]);

  useEffect(() => {
    if (!itemId || !brandedStatus) {
      setBranded(null);
      return undefined;
    }

    let active = true;
    sh.get(`/internal/cv_vault_items/${itemId}/branded_cv`)
      .then((res) => active && setBranded(res.data || null))
      .catch(() => active && setBranded(null));

    return () => {
      active = false;
    };
  }, [itemId, brandedStatus]);

  if (!item) {
    return (
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <EmptyState />
      </Box>
    );
  }

  const parsed = item.parsed || {};
  const tone = STATUS_TONE[item.status] || STATUS_TONE.uploaded;
  const isPdf = /\.pdf$/i.test(item.filename || "");
  const isDocx = /\.docx$/i.test(item.filename || "");
  const documentUrl = isDocx ? `/internal/cv_vault_items/${item.id}/file` : detail?.file_url;
  const running = ["queued", "parsing", "ocring"].includes(item.status);
  const failed = item.status === "failed" || item.status === "needs_ocr" || Boolean(item.stalled);
  const textPending = !detail?.cv_text && ["extracting_text", "ocring"].includes(item.status);
  const name = [parsed.first_name, parsed.last_name].filter(Boolean).join(" ");

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          px: 1.5,
          height: 44,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        }}
      >
        <Tooltip arrow placement="bottom-start" title={`${item.filename} · ${parserModelLabel(parserModel)}`}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 14.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
            }}
          >
            {name || item.filename}
          </Typography>
        </Tooltip>

        <Chip
          size="small"
          label={item.stalled ? "Stopped responding" : tone.label}
          sx={{
            height: 20,
            flexShrink: 0,
            fontSize: 10.5,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            borderRadius: "8px",
            color: item.stalled ? "hsl(0 72% 45%)" : tone.color,
            backgroundColor: item.stalled ? "hsl(0 80% 96%)" : tone.bg,
          }}
        />

        <ViewerTabs
          value={tab}
          onChange={setTab}
          hasDocument={Boolean(detail?.file_url) && (isPdf || isDocx)}
          hasText={Boolean(detail?.cv_text) || textPending}
          hasBranded={Boolean(brandedStatus)}
        />


        {brandedStatus === "ready" && (
          <Tooltip
            arrow
            placement="bottom"
            title={brandedIncomplete ? incompleteMessage(branded?.data) : "Download the PDF"}
          >
            <Box component="span" sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                disableRipple
                disabled={brandedIncomplete}
                component={brandedIncomplete ? "button" : "a"}
                href={brandedIncomplete ? undefined : `/internal/cv_vault_items/${item.id}/branded_cv/download`}
                download={brandedIncomplete ? undefined : branded?.filename}
                startIcon={<Download size={14} />}
                sx={{
                  ...ghostButtonSx,
                  "&.Mui-disabled": {
                    color: "hsl(150 8% 65%)",
                    backgroundColor: "hsl(140 8% 96%)",
                    border: "1px solid hsl(140 8% 90%)",
                  },
                }}
              >
                PDF
              </Button>
            </Box>
          </Tooltip>
        )}

        {item.extractable && !failed && (
          <Button size="small" disableRipple startIcon={<Wand2 size={14} />} onClick={() => onExtract(item, parserModel)} sx={primaryButtonSx}>
            Extract
          </Button>
        )}

        {failed && item.status !== "needs_ocr" && (
          <Button size="small" disableRipple startIcon={<RotateCcw size={14} />} onClick={() => onRetry(item, parserModel)} sx={ghostButtonSx}>
            Try again
          </Button>
        )}

        {item.status === "needs_ocr" && item.ocr_retryable && (
          <Button size="small" disableRipple startIcon={<ScanText size={14} />} onClick={() => onOcr(item)} sx={ghostButtonSx}>
            Read the scan again
          </Button>
        )}

        {running && (
          <Button
            size="small"
            disableRipple
            startIcon={<XCircle size={14} />}
            onClick={() => onCancel(item)}
            sx={{ ...ghostButtonSx, "&:hover": { color: "hsl(0 72% 45%)", borderColor: "hsl(0 72% 70%)" } }}
          >
            Cancel
          </Button>
        )}

        {Boolean(detail?.cv_text) && !brandedStatus && (
          <GenerateCvSplitButton onRun={(locale) => onGenerateBranded(item, locale, parserModel)} />
        )}

        {item.status === "parsed" && (
          <Button size="small" disableRipple startIcon={<UserPlus size={14} />} onClick={() => onConvert(item)} sx={primaryButtonSx}>
            Create candidate
          </Button>
        )}

        <Button
          size="small"
          disableRipple
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="More actions"
          sx={{ minWidth: 0, p: 0.75, flexShrink: 0, borderRadius: "6px", color: "hsl(150 10% 46%)" }}
        >
          <MoreHorizontal size={16} />
        </Button>

        <Button
          size="small"
          disableRipple
          onClick={onClose}
          aria-label="Close"
          sx={{ minWidth: 0, p: 0.75, flexShrink: 0, borderRadius: "6px", color: "hsl(150 10% 46%)", display: { md: "none" } }}
        >
          <X size={16} />
        </Button>

        <Menu
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {brandedStatus && brandedStatus !== "generating" && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                setRegenerateOpen(true);
              }}
              sx={{ fontSize: 13 }}
            >
              <Sparkles size={13} style={{ marginRight: 8 }} />
              Generate again
            </MenuItem>
          )}

          {brandedStatus === "ready" && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                setTab("branded");
                onEditingChange(true);
              }}
              sx={{ fontSize: 13 }}
            >
              <Pencil size={13} style={{ marginRight: 8 }} />
              Edit the Squarehub CV
            </MenuItem>
          )}

          {item.status !== "converted" && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                setReparseOpen(true);
              }}
              sx={{ fontSize: 13 }}
            >
              <Wand2 size={13} style={{ marginRight: 8 }} />
              Parse again
            </MenuItem>
          )}
          {item.status !== "converted" && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onDelete(item);
              }}
              sx={{ fontSize: 13, color: "hsl(0 72% 45%)", borderTop: "1px solid hsl(140 8% 92%)", mt: 0.5, pt: 1 }}
            >
              <Trash2 size={13} style={{ marginRight: 8 }} />
              Delete this CV
            </MenuItem>
          )}
        </Menu>

        <RegenerateCvDialog
          open={regenerateOpen}
          onClose={() => setRegenerateOpen(false)}
          onConfirm={({ locale, model }) => {
            setRegenerateOpen(false);
            onGenerateBranded(item, locale, model);
          }}
        />

        <ReparseCvDialog
          open={reparseOpen}
          onClose={() => setReparseOpen(false)}
          onConfirm={(model) => {
            setReparseOpen(false);
            onExtract(item, model);
          }}
        />
      </Box>

      <StatusStrip item={item} />

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        {editing && tab === "branded" && branded?.data && (
          <Box sx={{ width: { xs: "100%", md: "50%" }, flexShrink: 0, borderRight: "1px solid hsl(140 8% 88%)" }}>
            <BrandedCvEditor
              sourceUrl={`/internal/cv_vault_items/${item.id}/branded_cv`}
              data={branded.data}
              onSaved={(saved) => {
                setBranded(saved);
                setBrandedVersion((prev) => prev + 1);
              }}
              onClose={() => onEditingChange(false)}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", backgroundColor: "hsl(140 6% 92%)" }}>
          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={22} sx={{ color: "hsl(150 38% 17%)" }} />
            </Box>
          ) : tab === "branded" ? (
            <>
              {brandedIncomplete && (
                <Box
                  sx={{
                    flexShrink: 0,
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderBottom: "1px solid hsl(36 90% 80%)",
                    backgroundColor: "hsl(36 90% 97%)",
                  }}
                >
                  <AlertTriangle size={13} color="hsl(35 60% 28%)" style={{ flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 11.5, color: "hsl(35 60% 28%)" }}>
                    {`Not ready to send — ${missingLabels(branded?.data).join(", ").toLowerCase()} missing. Use Edit to complete it.`}
                  </Typography>
                </Box>
              )}
              <BrandedCvPane
                itemId={item.id}
                status={brandedStatus}
                stage={item.branded_cv_stage}
                progress={item.branded_cv_progress}
                stalled={item.branded_cv_stalled}
                error={branded?.error}
                onRegenerate={brandedStatus === "failed" ? () => onGenerateBranded(item, undefined, parserModel) : undefined}
                version={brandedVersion}
              />
            </>
          ) : (
            <CvViewer
              hideHeader
              fitWidth={isPdf}
              tab={tab}
              onTabChange={setTab}
              fileUrl={documentUrl}
              isPdf={isPdf}
              isDocx={isDocx}
              cvText={detail?.cv_text}
              textPending={textPending}
              textPendingLabel={item.status === "ocring" ? "Reading the scan..." : "Extracting the text..."}
              filename={item.filename}
            />
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default VaultDetail;
